import type { ColumnCategory } from '@/pages/price-planning/simulation/simulation-tool-page/utils/simulation-types';
import { connectColors, themeColors } from '@/theme/colors';
import { normalizeNumericString } from '@/utils/number';
import Big from 'big.js';
import type {
  MRT_Cell,
  MRT_Row,
  MRT_TableInstance,
} from 'material-react-table';
import { utils, writeFile } from 'xlsx';
import { priceDisplayDecimals, round } from './table-format-helpers';
import type {
  BaseRowData,
  BaseRowDataForDisplay,
  ColumnAttributes,
  EditableField,
  LockableField,
  NumericFilterValue,
} from './table-types';

export class MissingCurrencyDecimalsError extends Error {
  constructor(row: Pick<BaseRowData, 'productId' | 'priceUnitId'>) {
    super(
      `Currency decimals are missing for product ${row.productId} / price unit ${row.priceUnitId}.`,
    );
    this.name = 'MissingCurrencyDecimalsError';
  }
}

// Decimals of the row's own currency. No fallback: missing decimals are a data error.
export const getDecimals = (row: BaseRowData): number => {
  if (row.currencyDecimals == null) throw new MissingCurrencyDecimalsError(row);
  return row.currencyDecimals;
};

/**
 * Runs a pricing calculation for a UI edit. When a row is missing currency decimals the error is
 * passed to onMissingDecimals (to show the standard error toast) and null is returned, so the
 * caller aborts the edit without changing any data. Other errors are rethrown.
 */
export const runPricingEdit = <T,>(
  calculate: () => T,
  onMissingDecimals: (error: MissingCurrencyDecimalsError) => void,
): T | null => {
  try {
    return calculate();
  } catch (error) {
    if (error instanceof MissingCurrencyDecimalsError) {
      onMissingDecimals(error);
      return null;
    }
    throw error;
  }
};

/** Validates currency decimals for every row of a multi-row edit before any row is changed. */
export const hasCurrencyDecimalsForAll = (
  rows: BaseRowData[],
  onMissingDecimals: (error: MissingCurrencyDecimalsError) => void,
): boolean => {
  const missing = rows.find((row) => row.currencyDecimals == null);
  if (!missing) return true;
  onMissingDecimals(new MissingCurrencyDecimalsError(missing));
  return false;
};

/**
 * True when committing newValue would leave the stored price unchanged. Both sides are rounded to
 * the currency decimals: the edit box shows the stored value rounded, so a stored 56.004 left
 * untouched (shown as 56.00) must not count as an edit.
 */
export const isPriceEditUnchanged = (
  storedValue: number,
  newValue: number,
  decimals: number,
) => round(storedValue, decimals) === round(newValue, decimals);

// Save-time variant: same check, with a message that states the save was cancelled.
export const getDecimalsForSave = (row: BaseRowData): number => {
  try {
    return getDecimals(row);
  } catch (error) {
    if (error instanceof MissingCurrencyDecimalsError) {
      throw new Error(`${error.message} Nothing was saved.`);
    }
    throw error;
  }
};

// Display decimals for the own-currency price columns. Falls back to 2 so that rendering never
// fails; editing or saving such a row raises MissingCurrencyDecimalsError.
export const getDisplayDecimals = (row: Partial<BaseRowData>): number =>
  row.currencyDecimals ?? priceDisplayDecimals;

export const isRowChanged = <TRow extends BaseRowData>(
  original: TRow | undefined,
  current: TRow | undefined,
): boolean => {
  if (original === current) return false;
  if (!original || !current) return true;
  const ignoredKeys = new Set(['comment', 'isExemptFromFiltering']);
  return Object.keys(original).some(
    (key) =>
      !ignoredKeys.has(key) &&
      original[key as keyof TRow] !== current[key as keyof TRow],
  );
};

/** Compares only editable price fields (lp, rrp, rm, vat) between baseline
 *  and current row. Used for revert-button enabled state to avoid false
 *  positives from derived-field differences. */
export const isPriceFieldChanged = <TRow extends BaseRowData>(
  original: TRow | undefined,
  current: TRow | undefined,
): boolean => {
  if (original === current) return false;
  if (!original || !current) return true;
  return (
    original.lp !== current.lp ||
    original.rrp !== current.rrp ||
    original.rm !== current.rm ||
    original.vat !== current.vat
  );
};

export const isCellEditable = (
  columnKey: string,
  row: { useRrp?: boolean },
  lockedColumn: LockableField | undefined,
  columnData: Record<string, ColumnAttributes>,
): boolean => {
  if (columnKey === 'rrp' || columnKey === 'rm') {
    return (
      !!row.useRrp &&
      columnData[columnKey]?.columnType === 'editable' &&
      lockedColumn !== columnKey
    );
  }
  if (columnKey in columnData) {
    return columnData[columnKey]?.columnType === 'editable';
  }
  return false;
};

export const filterNonDistributorPricelistRows = <
  TRow extends { useRrp?: boolean },
>(
  rows: TRow[],
  field: EditableField,
): TRow[] => {
  if (field === 'rrp' || field === 'rm') {
    return rows.filter((row) => row.useRrp);
  }
  return rows;
};

export const computeBatchUpdate = <TRow extends BaseRowData>(
  dataMap: Map<string, TRow>,
  eligibleRows: TRow[],
  field: EditableField,
  value: number,
  isChangeProportional: boolean,
  lockedColumn: LockableField | undefined,
  initialData: Map<string, TRow>,
  calculateFn: (
    field: EditableField,
    row: TRow,
    value: number,
    lockedColumn: LockableField | undefined,
    originalLp: number,
  ) => TRow,
): Map<string, TRow> => {
  const newData = new Map(dataMap);
  for (const row of eligibleRows) {
    const primaryKey = row.primaryKey;
    const currentRow = newData.get(primaryKey) ?? dataMap.get(primaryKey);
    if (!currentRow) {
      continue;
    }
    const currentFieldValue = Number(currentRow[field] ?? 0);
    const originalLp = initialData.get(primaryKey)?.lp ?? currentRow.lp ?? 0;
    const actualValue = isChangeProportional
      ? currentFieldValue + currentFieldValue * (value / 100)
      : value;
    const updated = calculateFn(
      field,
      currentRow,
      actualValue,
      lockedColumn,
      originalLp,
    );
    if (updated) {
      newData.set(primaryKey, { ...updated, isExemptFromFiltering: true });
    }
  }
  return newData;
};

export const isCellChanged = (
  row: BaseRowDataForDisplay,
  field: keyof BaseRowDataForDisplay,
  formattedInitialData:
    | BaseRowDataForDisplay[]
    | Map<string, BaseRowDataForDisplay>,
) => {
  if (!row.primaryKey) return false;
  const originalRow =
    formattedInitialData instanceof Map
      ? formattedInitialData.get(row.primaryKey)
      : formattedInitialData.find((r) => r.primaryKey === row.primaryKey);
  if (!originalRow) return false;
  if (field in row && field in originalRow) {
    return (
      originalRow[field as keyof BaseRowDataForDisplay] !==
      row[field as keyof BaseRowDataForDisplay]
    );
  }
  return false;
};
export const getFontColor = (columnType?: 'editable') => {
  if (columnType === 'editable') {
    return themeColors.content.primary;
  }
  return connectColors.core.black;
};

// Formula: RRP= LP / (1 - RM) * (1 + VAT)
// VAT and RM are decimals (0.25 for 25%)
// kept at full precision — only round when displaying or saving to the backend
export const computeRrp = (lp: number, rm: number, vat: number) => {
  if (rm >= 1) return 0; // avoid division by zero
  const result = new Big(lp)
    .div(new Big(1).minus(rm))
    .times(new Big(1).plus(vat));
  return Number(result);
};

//formula LP = RRP / (1 + VAT) - (RRP / (1 + VAT)) * RM
// VAT and RM are decimals (0.25 for 25%)
// kept at full precision — only round when displaying or saving to the backend
export const computeLp = (rrp: number, rm: number, vat: number) => {
  const rrpOverVat = new Big(rrp).div(new Big(1).plus(vat));
  const result = rrpOverVat.minus(rrpOverVat.times(rm));
  return Number(result);
};

//formula RM% = (((RRP / (1 + VAT%)) - LP) / (RRP / (1 + VAT%)))
export const computeRm = (rrp: number, lp: number, vat: number) => {
  if (rrp === 0) return 0; // avoid division by zero
  const rrpOverVat = new Big(rrp).div(new Big(1).plus(vat));
  const result = rrpOverVat.minus(lp).div(rrpOverVat);
  return Number(result);
};

//formula LL RRP Index = (RRP / IER) / LL RRP * 100
export const computeLlRrpIndex = (rrp: number, llRrp: number, ier: number) => {
  if (rrp > 0 && llRrp > 0 && ier > 0) {
    const result = new Big(rrp).div(ier).div(llRrp).times(100);
    return Number(result);
  }
  return 0;
};

//formula LL LP Index = (LP / IER) / LL LP * 100
export const computeLlLpIndex = (lp: number, llLp: number, ier: number) => {
  if (lp > 0 && llLp > 0 && ier > 0) {
    const result = new Big(lp).div(ier).div(llLp).times(100);
    return Number(result);
  }
  return 0;
};

//formula LP FMC Factor = LP / FMC Local
export const computeLpFmcFactor = (lp: number, fmcLocal: number) => {
  if (fmcLocal > 0 && lp > 0) {
    const result = new Big(lp).div(fmcLocal);
    return Number(result);
  }
  return 0;
};

export const updateLpDerivedFields = (
  updated: BaseRowData,
  row: BaseRowData,
  localLeadIer: number,
) => {
  updated.lpIndexLocalLead = computeLlLpIndex(
    updated.lp,
    row.localLeadLp,
    localLeadIer,
  );
  updated.listPriceFmcFactor = computeLpFmcFactor(updated.lp, row.fmcLocal);
};

export const updateRrpDerivedFields = (
  updated: BaseRowData,
  row: BaseRowData,
  localLeadIer: number,
) => {
  updated.rrpIndexLocalLead = computeLlRrpIndex(
    updated.rrp,
    row.localLeadRrp,
    localLeadIer,
  );
};

/**
 * A future LP change is marked as "Yes" when there is a future
 * List Price that differs from the given List Price. If no future list price exists (nearestFutureModifiedLp is null),
 * or one that matches the given LP, it means no change is pending.
 * As name suggests it is the nearest future LP change only that is considered, not any future LP changes beyond that.
 */
export const calculateHasFutureLpChange = (
  lp: number,
  nearestFutureModifiedLp: number | null | undefined,
): boolean => nearestFutureModifiedLp != null && lp !== nearestFutureModifiedLp;

export const hasPriceChange = (
  row: BaseRowDataForDisplay,
  initialData: BaseRowDataForDisplay[] | Map<string, BaseRowDataForDisplay>,
): boolean => {
  const originalRow =
    initialData instanceof Map
      ? initialData.get(row.primaryKey)
      : initialData.find((r) => r.primaryKey === row.primaryKey);
  if (!originalRow) return false;
  return (
    row.rrp !== originalRow.rrp ||
    row.lp !== originalRow.lp ||
    row.rm !== originalRow.rm
  );
};

export const exportToExcel = (
  downloadType: 'all' | 'filtered',
  columnData: Record<string, ColumnAttributes>,
  data: BaseRowDataForDisplay[],
  filteredRows: BaseRowDataForDisplay[],
  columnVisibility: Record<string, boolean>,
  sheetName: string,
  fileName: string,
) => {
  const columnKeys = Object.keys(columnData);
  const columnsToExport =
    downloadType === 'all'
      ? columnKeys
      : columnKeys.filter((key) => columnVisibility[key] !== false);
  const rowsToExport = downloadType === 'all' ? data : filteredRows;
  const exportableData = rowsToExport.map((row) => {
    const entry: Record<string, unknown> = {};
    for (const key of columnsToExport) {
      const col = columnData[key];
      const header = col?.exportLabel ?? col?.label ?? key;
      const rawValue = row[key as keyof BaseRowDataForDisplay];
      if (NUMERIC_FILTER_COLUMNS.has(key)) {
        const parsed = parseNumericInput(
          rawValue as number | string | undefined,
        );
        entry[header] =
          parsed != null && PERCENTAGE_COLUMNS.has(key)
            ? parsed / 100
            : (parsed ?? null);
      } else {
        entry[header] = rawValue;
      }
    }
    return entry;
  });

  const wb = utils.book_new();
  const ws = utils.json_to_sheet(exportableData);

  if (ws['!ref']) {
    const { e } = utils.decode_range(ws['!ref']);
    columnsToExport.forEach((key, c) => {
      if (!PERCENTAGE_COLUMNS.has(key)) return;
      for (let r = 1; r <= e.r; r++) {
        const cell = ws[utils.encode_cell({ r, c })];
        if (cell) cell.z = '0.00%';
      }
    });
  }

  utils.book_append_sheet(wb, ws, sheetName);
  writeFile(wb, fileName);
};

export const getHeaderCategoryBackgroundColor = (category?: ColumnCategory) => {
  switch (category) {
    case 'priceList':
      return connectColors.core.yellow[50];
    case 'productData':
      return connectColors.core.azure[200];
    case 'localLead':
      return connectColors.core.slate[200];
    case 'localAsIs':
      return connectColors.core.teal[200];
    case 'pricingSimulation':
      return connectColors.core.blue[200];
    case 'currentYear':
      return connectColors.core.blue[200];
    case 'simulationResult':
      return connectColors.core.blue[200];
    case 'other':
      return connectColors.core.gray[200];
    default:
      return connectColors.core.blue[200];
  }
};

export const getHeaderCategorySubBackgroundColor = (
  category?: ColumnCategory,
) => {
  switch (category) {
    case 'priceList':
      return '#FFFCDA'; //color not present in Connect
    case 'productData':
      return connectColors.core.azure[50];
    case 'localLead':
      return connectColors.core.slate[50];
    case 'localAsIs':
      return connectColors.core.teal[50];
    case 'pricingSimulation':
      return connectColors.core.blue[50];
    case 'currentYear':
      return connectColors.core.blue[50];
    case 'simulationResult':
      return connectColors.core.blue[50];
    case 'other':
      return connectColors.core.gray[50];
    default:
      return connectColors.core.blue[50];
  }
};

//table-core specific helpers
export const getFilterOptions = <TRowForDisplay extends BaseRowDataForDisplay>(
  key: string,
  data: TRowForDisplay[],
): { id: string; label: string }[] => {
  const adjustedData = data.map(
    (row) => (row[key as keyof TRowForDisplay] as string | boolean) ?? '',
  );
  return Array.from(new Set(adjustedData.map((value) => String(value ?? ''))))
    .sort(compareNumericValues)
    .map((normalized) => {
      return {
        label: normalized === '' ? '(empty)' : normalized,
        id: normalized,
      };
    });
};

export const handleCellKeyDown = <
  TRowForDisplay extends BaseRowDataForDisplay,
  TColumn extends string,
>({
  event,
  cell,
  column,
  table,
  isCellEditable,
  handleCellEdit,
  setPendingEditKey,
}: {
  event: React.KeyboardEvent<HTMLTableCellElement>;
  cell: MRT_Cell<TRowForDisplay>;
  column: { id: string };
  table: MRT_TableInstance<TRowForDisplay>;
  isCellEditable: (columnKey: TColumn, row: TRowForDisplay) => boolean;
  handleCellEdit: (
    value: string,
    columnKey: string,
    row: MRT_Row<TRowForDisplay>,
  ) => void;
  setPendingEditKey: (key: string | null) => void;
}) => {
  const isCurrentlyEditing = table.getState().editingCell?.id === cell.id;

  if (
    ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key) &&
    isCurrentlyEditing
  ) {
    event.stopPropagation();
  }

  if (event.key === 'Enter') {
    if (!isCurrentlyEditing) {
      event.preventDefault();
      if (isCellEditable?.(column.id as TColumn, cell.row.original)) {
        table.setEditingCell(cell);
      }
    } else {
      // for the RRP, don't handle Enter here
      // let rrpPricePointsDropdown handle it
      if (column.id === 'rrp') {
        return;
      }
      const textField = table.refs.editInputRefs.current?.[column.id];
      const newValue = textField?.value;
      if (newValue !== undefined) {
        handleCellEdit(newValue, column.id, cell.row);
      }
      table.setEditingCell(null);
      event.currentTarget?.focus();
      return;
    }
  }

  if (event.key === 'Escape' && isCurrentlyEditing) {
    event.preventDefault();
    table.setEditingCell(null);
    event.currentTarget?.focus();
    return;
  }

  const isNumericKey =
    (event.key >= '0' && event.key <= '9') ||
    event.key === '.' ||
    event.key === ',' ||
    event.key === '-';

  if (
    !isCurrentlyEditing &&
    isNumericKey &&
    isCellEditable?.(column.id as TColumn, cell.row.original)
  ) {
    event.preventDefault();
    setPendingEditKey(event.key);
    table.setEditingCell(cell);
  }
};

export const getColumnOrder = <TColumn extends string>(
  groupedBy: 'rm' | 'rrp' | undefined,
  columnKeys: TColumn[],
): string[] => {
  const groupingColumns =
    groupedBy === 'rm'
      ? ['localLeadRm', 'rm', 'productCount']
      : groupedBy === 'rrp'
        ? ['localLeadRrp', 'rrp', 'rrpIndexLocalLead', 'productCount']
        : [];
  const otherColumns = columnKeys.filter(
    (key) => !groupingColumns.includes(key),
  );
  return [
    'mrt-row-select',
    ...groupingColumns,
    ...otherColumns,
    'mrt-row-actions',
  ];
};

export const getCommentFilterOptions = (): { id: string; label: string }[] => {
  return [
    { id: 'hasComment', label: 'Has comment' },
    { id: 'noComment', label: 'No comment' },
  ];
};

export const NUMERIC_FILTER_COLUMNS = new Set([
  'localLeadRrp',
  'localLeadRm',
  'localLeadLp',
  'asIsRrp',
  'asIsRm',
  'asIsLp',
  'rrp',
  'rm',
  'lp',
  'rrpIndexLocalLead',
  'lpIndexLocalLead',
  'lpDelta',
  'nip',
  'llNip',
  'vat',
  'listPriceFmcFactor',
  'listPriceIncreaseCo',
  'fmcLocal',
  'forecastedDemand',
  'globalLeadRrp',
  'rrpIndexGlobalLead',
  'lpIndexGlobalLead',
  'localLeadCurrencyRate',
  'llNipIndex',
]);

export const PERCENTAGE_COLUMNS = new Set([
  'localLeadRm',
  'asIsRm',
  'rm',
  'lpDelta',
  'vat',
]);

export const NUMERIC_FILTER_MODE_OPTIONS = [
  { id: 'values', label: 'Values' },
  { id: 'betweenInclusive', label: 'Between (inclusive)' },
  { id: 'betweenExclusive', label: 'Between (exclusive)' },
  { id: 'greaterThan', label: 'Greater than' },
  { id: 'greaterThanOrEqual', label: 'Greater than or equal' },
  { id: 'lessThan', label: 'Less than' },
  { id: 'lessThanOrEqual', label: 'Less than or equal' },
] as const;

export const parseNumericInput = (
  input: number | string | undefined,
): number | undefined => {
  if (typeof input === 'number') return input;
  if (!input?.trim()) return undefined;
  const cleaned = normalizeNumericString(input.replace(/[\s%]/g, ''));
  const num = Number.parseFloat(cleaned);
  return Number.isNaN(num) ? undefined : num;
};

export const compareNumericValues = (
  first: number | string | undefined,
  second: number | string | undefined,
): number => {
  const firstNumber = parseNumericInput(first);
  const secondNumber = parseNumericInput(second);

  if (firstNumber !== undefined && secondNumber !== undefined) {
    return firstNumber - secondNumber;
  }
  if (firstNumber === undefined && secondNumber !== undefined) return -1;
  if (firstNumber !== undefined && secondNumber === undefined) return 1;

  return String(first ?? '').localeCompare(String(second ?? ''));
};

export const applyNumericFilter = (
  cellValue: number | string | undefined,
  filterValue: NumericFilterValue | undefined,
): boolean => {
  if (!filterValue) return true;

  const { mode, selectedValues, min, max, value } = filterValue;

  if (mode === 'values') {
    if (!selectedValues?.length) return true;
    const strValue =
      typeof cellValue === 'number' ? cellValue.toString() : cellValue;
    return selectedValues.includes(strValue ?? '');
  }

  const numValue = parseNumericInput(cellValue);
  if (numValue === undefined) return false;

  switch (mode) {
    case 'betweenInclusive':
      if (min !== undefined && numValue < min) return false;
      if (max !== undefined && numValue > max) return false;
      return true;
    case 'betweenExclusive':
      if (min !== undefined && numValue <= min) return false;
      if (max !== undefined && numValue >= max) return false;
      return true;
    case 'greaterThan':
      return value !== undefined ? numValue > value : true;
    case 'greaterThanOrEqual':
      return value !== undefined ? numValue >= value : true;
    case 'lessThan':
      return value !== undefined ? numValue < value : true;
    case 'lessThanOrEqual':
      return value !== undefined ? numValue <= value : true;
    default:
      return true;
  }
};

export const matchesColumnFilter = (
  cellText: string,
  columnId: string,
  filterValue: NumericFilterValue | string[] | undefined,
  numericFilterColumns: Set<string>,
): boolean => {
  if (numericFilterColumns.has(columnId)) {
    return applyNumericFilter(cellText, filterValue as NumericFilterValue);
  }

  const selectedValues = filterValue as string[] | undefined;

  if (columnId === 'comment') {
    const hasComment = cellText !== '';
    if (selectedValues?.includes('hasComment')) return hasComment;
    if (selectedValues?.includes('noComment')) return !hasComment;
    return true;
  }

  return selectedValues?.includes(cellText) ?? true;
};
